/**
 * Fila offline das confirmações de venda (localStorage + pedido de envio do Service Worker).
 *
 * Item: { id, url, method, body, token, frentista_id, meta, addedAt, tentativas, tentativas409, bloqueio }
 *  - token: o do VENDEDOR QUE FEZ A VENDA, gravado no enqueue. O reenvio usa esse token, sem o
 *    interceptor do api.js (que punha o token de quem está logado agora: na troca de turno o
 *    confirm voltava 404 e a venda era descartada em silêncio, plano 19/10 item 4.12).
 *  - Itens gravados pela versão anterior trazem o token em headers.Authorization; continuam valendo.
 *  - meta: cliente, placa, valor e vendedor, só para os avisos.
 *
 * Resultado do reenvio de cada item:
 *  - 2xx -> sai da fila. Inclui 200 com ja_finalizado (o confirmar é idempotente: venda que já
 *    tinha sido confirmada não gera aviso falso). 2xx com "aviso" sai da fila COM AVISO visível e
 *    registrado, nunca em silêncio:
 *      - tipo "ocorrencia" (padrão, regra 1 aprovada pelo cliente em 24/10/2026): o prêmio do
 *        sorteio ou a etapa da Rota deixou de valer no meio do atendimento; a venda foi finalizada
 *        COM o desconto calculado e a ocorrência vai para o Master e o caixa. Não há diferença a cobrar.
 *      - tipo "ajuste" (só com "valores_ajustados", regra anterior do backend): a venda foi
 *        finalizada sem o desconto, com outro valor; o gerente confere a diferença no caixa.
 *  - sem resposta (rede caiu de novo) -> para e tenta tudo na próxima vez.
 *  - 5xx, 408, 429 -> fica para a próxima tentativa.
 *  - 401/403 -> FICA na fila, marcada como "aguardando login" (token vencido). Se o MESMO vendedor
 *    estiver logado com um token novo, o reenvio tenta de novo com ele. Avisa uma vez.
 *  - 409 (venda mudou durante a confirmação) -> fica e tenta de novo; na 5ª vez sai com aviso.
 *  - demais 4xx (400, 404, 422...) -> sai da fila COM AVISO visível e registrado (nunca em silêncio).
 *
 * Venda aguardando login há mais de HORAS_TIRAR_BLOQUEADA horas (vendedor que não volta a entrar
 * neste aparelho): a conferência oferece tirá-la da fila, com confirmação; ela vira aviso de venda
 * não sincronizada, para o gerente conferir no Master. Nunca sai sozinha.
 */
import { replayApi } from "@/lib/api";
import { modal } from "@/lib/modal";

const KEY = "offline_queue";
const KEY_AVISOS = "offline_queue_avisos";
const MAX_AVISOS = 50;
export const MAX_TENTATIVAS_409 = 5;
export const HORAS_TIRAR_BLOQUEADA = 24;

// ---------- armazenamento (localStorage pode falhar: aba anônima, cota cheia) ----------
function lerLista(chave) {
  try {
    const v = JSON.parse(localStorage.getItem(chave) || "[]");
    return Array.isArray(v) ? v : [];
  } catch { return []; }
}
function gravarLista(chave, arr) {
  try { localStorage.setItem(chave, JSON.stringify(arr)); return true; }
  catch { return false; }
}
function avisarMudanca() {
  try { window.dispatchEvent(new CustomEvent("offline-queue-changed")); } catch { /* sem window */ }
}
const read = () => lerLista(KEY);
function write(arr) {
  const ok = gravarLista(KEY, arr);
  avisarMudanca();
  return ok;
}
// Alterações por id sobre a fila ATUAL: uma venda guardada durante o envio não se perde.
function remover(id) { write(read().filter((i) => i.id !== id)); }
function atualizar(id, campos) { write(read().map((i) => (i.id === id ? { ...i, ...campos } : i))); }

function novoId() {
  try { if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID(); } catch { /* http sem crypto */ }
  return `f${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

// ---------- token ----------
function tokenDoItem(item) {
  if (item?.token) return item.token;
  const h = item?.headers?.Authorization || item?.headers?.authorization || "";
  const t = String(h).replace(/^Bearer\s+/i, "").trim();
  return t || null;
}
function tokenAtual() {
  try { return localStorage.getItem("frentista_token") || null; } catch { return null; }
}
/** Lê o payload do JWT (sem validar: só para saber de quem é o token). */
export function jwtPayload(token) {
  try {
    const p = String(token || "").split(".")[1];
    if (!p) return null;
    const b64 = p.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(b64 + "=".repeat((4 - (b64.length % 4)) % 4)));
  } catch { return null; }
}
function donoDoItem(item) {
  return item?.frentista_id || jwtPayload(tokenDoItem(item))?.sub || null;
}

// ---------- textos dos avisos ----------
function detalheErro(e) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string" && d) return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || "").filter(Boolean).join(" ");
  return e?.message || "Erro inesperado";
}
function idDaUrl(url) {
  const m = /\/atendimentos\/([^/]+)\/confirmar/.exec(String(url || ""));
  return m ? m[1] : "";
}
function brl(v) {
  const n = Number(v);
  if (v == null || !Number.isFinite(n)) return "";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}
function descreverVenda(x) {
  const partes = [x.cliente_nome || "Cliente", x.placa ? `placa ${x.placa}` : "", brl(x.valor_liquido)].filter(Boolean);
  const quando = x.adicionado_em ? new Date(x.adicionado_em).toLocaleString("pt-BR") : "";
  return `${partes.join(" · ")}${quando ? ` (${quando})` : ""}`;
}
function resumoItem(item) {
  const m = item.meta || {};
  return {
    atendimento_id: m.atendimento_id || idDaUrl(item.url),
    cliente_nome: m.cliente_nome || "", placa: m.placa || "",
    valor_liquido: m.valor_liquido ?? null, frentista_nome: m.frentista_nome || "",
    adicionado_em: item.addedAt || null,
  };
}
function novoAviso(item, status, motivo, extra = {}) {
  return { id: item.id, quando: Date.now(), status: status || null, motivo: motivo || "", ...resumoItem(item), ...extra };
}
function motivoAviso(a) {
  return a.motivo || (a.status ? `erro ${a.status}` : "erro");
}
/** Avisos de venda que SINCRONIZOU (não é descarte): com valor ajustado pelo servidor, ou com
 * ocorrência registrada e o desconto mantido. */
const ehAjuste = (a) => a?.tipo === "ajuste";
const ehOcorrencia = (a) => a?.tipo === "ocorrencia";
const ehSincronizada = (a) => ehAjuste(a) || ehOcorrencia(a);
function linhaAviso(a) {
  const rotulo = ehAjuste(a) ? "Sincronizada com ajuste"
    : ehOcorrencia(a) ? "Sincronizada com ocorrência" : "Não sincronizada";
  return `${rotulo}: ${descreverVenda(a)}: ${motivoAviso(a)}`;
}
function avisarAjuste(lista) {
  modal.warning({
    title: lista.length === 1 ? "Venda sincronizada com ajuste" : `${lista.length} vendas sincronizadas com ajuste`,
    message: "Venda feita sem internet foi confirmada no servidor com outro valor. "
      + "Avise o gerente para conferir a diferença no caixa.",
    details: lista.map((a) => `${descreverVenda(a)}: ${motivoAviso(a)}`),
  });
}
function avisarOcorrencia(lista) {
  modal.warning({
    title: lista.length === 1 ? "Venda sincronizada com ocorrência" : `${lista.length} vendas sincronizadas com ocorrência`,
    message: "Venda feita sem internet foi confirmada no servidor com o desconto calculado. "
      + "A ocorrência foi registrada para o Master e aparece no caixa; não cobre diferença do cliente.",
    details: lista.map((a) => `${descreverVenda(a)}: ${motivoAviso(a)}`),
  });
}
function registrarAvisos(novos) {
  gravarLista(KEY_AVISOS, [...lerLista(KEY_AVISOS), ...novos].slice(-MAX_AVISOS));
  avisarMudanca();
}
function avisarDescarte(lista) {
  modal.warning({
    title: lista.length === 1 ? "Venda não sincronizada" : `${lista.length} vendas não sincronizadas`,
    message: "Venda feita sem internet não foi confirmada no servidor e saiu da fila deste aparelho. "
      + "Avise o gerente para conferir no Master.",
    details: lista.map((a) => `${descreverVenda(a)}: ${motivoAviso(a)}`),
  });
}
function avisarBloqueio(itens) {
  const nomes = [...new Set(itens.map((i) => i.meta?.frentista_nome).filter(Boolean))];
  modal.warning({
    title: "Venda aguardando login",
    message: `A sessão de ${nomes.length ? nomes.join(", ") : "quem fez a venda"} expirou. A venda continua `
      + "guardada neste aparelho e será enviada quando esse vendedor entrar de novo no sistema.",
    details: itens.map((i) => descreverVenda(resumoItem(i))),
  });
}

function desdeBloqueio(item) {
  return Number(item?.bloqueio?.desde) || Number(item?.addedAt) || 0;
}
/** Aguardando login há mais de HORAS_TIRAR_BLOQUEADA horas (pode ser tirada da fila, com confirmação). */
export function bloqueadaHaMuito(item, agora = Date.now()) {
  const desde = desdeBloqueio(item);
  return !!item?.bloqueio && desde > 0 && agora - desde >= HORAS_TIRAR_BLOQUEADA * 3600 * 1000;
}
function quandoTexto(ms) {
  try { return new Date(ms).toLocaleString("pt-BR"); } catch { return ""; }
}

// ---------- API pública ----------
export function enqueue(req) {
  const { headers, ...resto } = req || {};
  const item = {
    ...resto,
    token: resto.token || tokenDoItem({ headers }) || null,
    id: novoId(),
    addedAt: Date.now(),
    tentativas: 0,
  };
  const arr = read();
  arr.push(item);
  if (!write(arr)) {
    // Sem espaço/armazenamento bloqueado: nunca perde a venda em silêncio.
    modal.error({
      title: "Venda não guardada",
      message: "Não foi possível guardar a venda offline neste aparelho. Avise o gerente.",
      details: [descreverVenda(resumoItem(item))],
    });
  }
  // Pede ao Service Worker um envio em segundo plano quando a rede voltar.
  try {
    if ("serviceWorker" in navigator && "SyncManager" in window) {
      navigator.serviceWorker.ready.then((reg) => reg.sync.register("sync-atendimentos").catch(() => {}));
    }
  } catch { /* sem SW */ }
  return item.id;
}

export function pendingCount() { return read().length; }

/** Situação para o indicador: total na fila, quantos aguardam login e avisos não lidos. */
export function estadoFila() {
  const arr = read();
  const bloqueados = arr.filter((i) => i.bloqueio);
  return {
    total: arr.length,
    bloqueados: bloqueados.length,
    vendedores_bloqueados: [...new Set(bloqueados.map((i) => i.meta?.frentista_nome).filter(Boolean))],
    avisos: lerLista(KEY_AVISOS).length,
    avisos_ajuste: lerLista(KEY_AVISOS).filter(ehAjuste).length,
    avisos_ocorrencia: lerLista(KEY_AVISOS).filter(ehOcorrencia).length,
  };
}

export function listarAvisos() { return lerLista(KEY_AVISOS); }
export function limparAvisos() { gravarLista(KEY_AVISOS, []); avisarMudanca(); }

/**
 * Tira da fila as vendas aguardando login há mais de HORAS_TIRAR_BLOQUEADA horas (ids escolhidos
 * na conferência). Cada uma vira aviso registrado. Espera um envio em andamento terminar e só tira
 * o que AINDA está bloqueado: venda que sincronizou nesse meio tempo não gera aviso falso.
 */
export async function tirarBloqueadasDaFila(ids) {
  if (emAndamento) { try { await emAndamento; } catch { /* o envio trata os próprios erros */ } }
  const alvo = new Set(ids || []);
  const agora = Date.now();
  const sair = read().filter((i) => alvo.has(i.id) && bloqueadaHaMuito(i, agora));
  if (!sair.length) return 0;
  const saem = new Set(sair.map((i) => i.id));
  write(read().filter((i) => !saem.has(i.id)));
  registrarAvisos(sair.map((i) => novoAviso(i, i.bloqueio?.status,
    `tirada da fila neste aparelho: aguardava o login de ${i.meta?.frentista_nome || "quem fez a venda"}`
    + ` desde ${quandoTexto(desdeBloqueio(i))}`)));
  return sair.length;
}

async function oferecerTirarBloqueadas(antigas, details) {
  const n = antigas.length;
  const tirar = await modal.confirm({
    title: "Vendas guardadas neste aparelho",
    message: `${n === 1 ? "1 venda aguarda" : `${n} vendas aguardam`} há mais de ${HORAS_TIRAR_BLOQUEADA} horas o login `
      + "de quem fez a venda. Se esse vendedor não vai voltar a entrar neste aparelho, tire da fila: "
      + "a venda vira um aviso de venda não sincronizada, para o gerente conferir no Master.",
    details,
    confirmText: n === 1 ? "Tirar da fila" : `Tirar ${n} da fila`,
    cancelText: "Manter guardadas",
  });
  if (!tirar) return;
  const certeza = await modal.confirm({
    title: n === 1 ? "Tirar 1 venda da fila?" : `Tirar ${n} vendas da fila?`,
    message: "Este aparelho não vai mais enviar essa venda ao servidor. Ela fica registrada como aviso "
      + "até o gerente conferir no Master. Não é possível desfazer.",
    details: antigas.map((i) => `${i.meta?.frentista_nome || "Vendedor"}: ${descreverVenda(resumoItem(i))}`),
    confirmText: "Sim, tirar da fila",
    cancelText: "Voltar",
    tone: "danger",
  });
  if (certeza) await tirarBloqueadasDaFila(antigas.map((i) => i.id));
}

/**
 * Conferência (toque no indicador): vendas aguardando login + vendas que saíram da fila.
 * foco "bloqueados" (botão "aguardando login"): se houver venda bloqueada há mais de
 * HORAS_TIRAR_BLOQUEADA horas, oferece tirá-la da fila (com confirmação).
 */
export async function mostrarSituacaoFila(foco) {
  const bloqueados = read().filter((i) => i.bloqueio);
  const avisos = listarAvisos();
  const details = [
    ...bloqueados.map((i) => `Aguardando login de ${i.meta?.frentista_nome || "quem fez a venda"}: ${descreverVenda(resumoItem(i))}`),
    ...avisos.map(linhaAviso),
  ];
  if (!details.length) return;
  const agora = Date.now();
  const antigas = bloqueados.filter((i) => bloqueadaHaMuito(i, agora));
  if (foco === "bloqueados" && antigas.length) {
    await oferecerTirarBloqueadas(antigas, details);
    return;
  }
  if (!avisos.length) {
    await modal.warning({
      title: "Vendas guardadas neste aparelho",
      message: "Elas serão enviadas quando o vendedor que fez a venda entrar de novo no sistema. "
        + `Depois de ${HORAS_TIRAR_BLOQUEADA} horas aguardando, este aviso permite tirar a venda da fila.`,
      details,
    });
    return;
  }
  const limpar = await modal.confirm({
    title: avisos.every(ehAjuste) ? "Vendas sincronizadas com ajuste"
      : avisos.every(ehOcorrencia) ? "Vendas sincronizadas com ocorrência"
        : avisos.some(ehSincronizada) ? "Vendas para conferir" : "Vendas não sincronizadas",
    message: "Confira estas vendas no Master. As que aguardam login continuam guardadas.",
    details,
    confirmText: "Já conferi, limpar avisos",
    cancelText: "Fechar",
  });
  if (limpar) limparAvisos();
}

async function reenviar(item) {
  const tokenItem = tokenDoItem(item);
  const atual = tokenAtual();
  const tokens = tokenItem ? [tokenItem] : [];
  if (atual && atual !== tokenItem) {
    const dono = donoDoItem(item);
    // Token do item vencido: o MESMO vendedor, logado de novo, tem um token novo.
    // Item sem token nenhum (gravado sem login): a única opção é o token de quem está logado.
    if (!tokenItem || (dono && jwtPayload(atual)?.sub === dono)) tokens.push(atual);
  }
  if (!tokens.length) return { ok: false, status: 401, msg: "Sem login do vendedor neste aparelho" };
  let ultimo = null;
  for (const tk of tokens) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const resp = await replayApi.request({
        url: item.url,
        method: item.method || "POST",
        data: item.body ?? {},
        headers: { Authorization: `Bearer ${tk}`, "X-Offline-Replay": "1" },
      });
      return { ok: true, data: resp?.data };
    } catch (e) {
      const status = e?.response?.status;
      ultimo = { ok: false, status, msg: detalheErro(e) };
      if (status !== 401 && status !== 403) return ultimo; // só 401/403 tentam o próximo token
    }
  }
  return ultimo;
}

async function processar() {
  const res = { sent: 0, failed: 0, kept: 0, blocked: 0, discarded: 0, ja_finalizados: 0, ajustados: 0, ocorrencias: 0 };
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    res.kept = read().length;
    return res;
  }
  const lote = read();
  if (!lote.length) return res;
  const descartes = [];
  const ajustes = [];
  const novosBloqueios = [];
  for (let i = 0; i < lote.length; i += 1) {
    const item = lote[i];
    // eslint-disable-next-line no-await-in-loop
    const r = await reenviar(item);
    if (r.ok) {
      remover(item.id);
      res.sent += 1;
      if (r.data?.ja_finalizado) res.ja_finalizados += 1;
      // G6: confirmada com aviso. Com "valores_ajustados" o servidor cobrou outro valor (regra
      // anterior); sem eles, manteve o desconto e só registrou a ocorrência. Nunca em silêncio.
      if (r.data?.aviso) {
        const bruto = r.data?.valores_ajustados?.valor_liquido;
        const v = bruto == null || bruto === "" ? NaN : Number(bruto);
        ajustes.push(novoAviso(item, 200, String(r.data.aviso), Number.isFinite(v)
          ? { tipo: "ajuste", valor_ajustado: v }
          : { tipo: "ocorrencia", valor_ajustado: null }));
      }
      continue;
    }
    const st = r.status;
    if (!st) { // sem resposta: a rede caiu de novo; o resto fica para a próxima vez
      res.failed += lote.length - i;
      break;
    }
    if (st >= 500 || st === 408 || st === 429) {
      atualizar(item.id, { tentativas: (item.tentativas || 0) + 1, ultimo_erro: r.msg });
      res.failed += 1;
      continue;
    }
    if (st === 401 || st === 403) {
      if (!item.bloqueio) novosBloqueios.push(item);
      atualizar(item.id, { bloqueio: { status: st, motivo: r.msg, desde: item.bloqueio?.desde || Date.now() } });
      res.blocked += 1;
      continue;
    }
    if (st === 409) {
      const t = (item.tentativas409 || 0) + 1;
      if (t < MAX_TENTATIVAS_409) {
        atualizar(item.id, { tentativas409: t, ultimo_erro: r.msg });
        res.failed += 1;
        continue;
      }
    }
    remover(item.id);
    descartes.push(novoAviso(item, st, r.msg));
    res.discarded += 1;
  }
  res.kept = read().length;
  if (descartes.length || ajustes.length) registrarAvisos([...descartes, ...ajustes]);
  if (descartes.length) avisarDescarte(descartes);
  if (ajustes.some(ehAjuste)) avisarAjuste(ajustes.filter(ehAjuste));
  if (ajustes.some(ehOcorrencia)) avisarOcorrencia(ajustes.filter(ehOcorrencia));
  res.ajustados = ajustes.filter(ehAjuste).length;
  res.ocorrencias = ajustes.filter(ehOcorrencia).length;
  if (novosBloqueios.length) avisarBloqueio(novosBloqueios);
  return res;
}

// Um envio por vez: online + Service Worker + indicador podem pedir ao mesmo tempo.
let emAndamento = null;
export function flushQueue() {
  if (emAndamento) return emAndamento;
  emAndamento = processar().finally(() => { emAndamento = null; });
  return emAndamento;
}

// Envia sozinho quando a rede volta.
try { window.addEventListener("online", () => { flushQueue(); }); } catch { /* sem window */ }

// Pedido de envio vindo do Service Worker (Background Sync).
try {
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker?.addEventListener?.("message", (event) => {
      if (event.data?.type === "FLUSH_QUEUE_REQUEST") flushQueue();
    });
  }
} catch { /* sem SW */ }
